import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import CONSTANTS from "../../../shared/constants";

const STATUS_CHIP = {
  active: { label: "Active", bg: "#DCFCE7", color: "#166534" },
  needs_setup: { label: "Needs Setup", bg: "#FEF3C7", color: "#92400E" },
  cancellation_scheduled: {
    label: "Cancellation Scheduled",
    bg: "#FEF3C7",
    color: "#92400E",
  },
};

const FINDING_SEVERITY = {
  critical: "error",
  high: "error",
  warning: "warning",
  medium: "warning",
  low: "info",
  info: "info",
};

const AgentStatusChip = ({ status }) => {
  const cfg = STATUS_CHIP[status] || {
    label: status || "—",
    bg: "#F3F4F6",
    color: "#6B7280",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: "11px",
        height: "20px",
      }}
    />
  );
};

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      textTransform: "uppercase",
      fontSize: "11px",
      fontWeight: 700,
      color: "rgba(1,12,20,0.6)",
      letterSpacing: "0.05em",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const KeyValue = ({ label, value, mono }) => (
  <Stack>
    <Typography sx={{ fontSize: "11px", color: "rgba(1,12,20,0.6)" }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: mono ? "monospace" : undefined,
      }}
    >
      {value ?? "—"}
    </Typography>
  </Stack>
);

// UUIDs are 36 chars; rendered in full they dominate the surrounding text
// and push real labels off the right edge. Show a short prefix in the row
// itself and the full value on hover; the copy button always copies the
// full string so downstream lookups still work.
const UUID_LIKE_LENGTH = 24;

const CopyableMono = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value)
    return (
      <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>—</Typography>
    );
  const onCopy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const display =
    value.length > UUID_LIKE_LENGTH ? `${value.slice(0, 8)}…` : value;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title={value}>
        <Typography sx={{ fontSize: "12px", fontFamily: "monospace" }}>
          {display}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? "Copied" : `Copy ${label || "value"}`}>
        <IconButton size="small" onClick={onCopy} sx={{ p: 0.25 }}>
          {copied ? (
            <CheckIcon sx={{ fontSize: 14, color: "#166534" }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// All deep links resolve through the env-aware base URLs in CONSTANTS so
// they target the right host in dev / staging / prod. Stripe drops the
// /test/ prefix in PROD via CONSTANTS.STRIPE_DASHBOARD_BASE.
const stripeCustomerUrl = (customerId) =>
  customerId
    ? `${CONSTANTS.STRIPE_DASHBOARD_BASE}/customers/${customerId}`
    : null;

const stripeSubscriptionUrl = (subscriptionId) =>
  subscriptionId
    ? `${CONSTANTS.STRIPE_DASHBOARD_BASE}/subscriptions/${subscriptionId}`
    : null;

const contractDetailUrl = (contractId) =>
  contractId
    ? `${CONSTANTS.CUSTOMER_FE_BASE}/employee-portal/billing/contracts/${contractId}`
    : null;

// ElevenLabs's conversational-AI agent URL. Verified against current dashboard
// routing as of 2026-05; surface as a deep link so support can click through
// without leaving the modal. If the route ever moves, this stays a useful
// "search term" — opening the dashboard root still finds the agent.
const elevenLabsAgentUrl = (agentId) =>
  agentId
    ? `https://elevenlabs.io/app/conversational-ai/agents/${agentId}`
    : null;

const callHistoryUrl = (companyId) =>
  companyId
    ? `${CONSTANTS.CUSTOMER_FE_BASE}/voice-agent-calls?company=${companyId}`
    : null;

// Per-agent call-history link: the customer FE call list hydrates its
// ``location`` filter from ``?configuration_location=<csv>`` on mount, so
// passing all of the agent's ConfigurationLocation ids narrows the page to
// just that agent's calls. Returns null if there are no live locations
// (which is also when the link wouldn't surface anything).
const callHistoryUrlForAgent = (companyId, locations) => {
  if (!companyId) return null;
  const ids = (locations || []).map((l) => l.id).filter(Boolean);
  if (!ids.length) return null;
  return `${CONSTANTS.CUSTOMER_FE_BASE}/voice-agent-calls?company=${companyId}&configuration_location=${ids.join(",")}`;
};

const voiceAgentsAppUrl = (companyId) =>
  companyId
    ? `${CONSTANTS.CUSTOMER_FE_BASE}/voice-agents?company=${companyId}`
    : null;

export const AccountDetailsModal = ({ open, onClose, row }) => {
  const [jsonCopied, setJsonCopied] = useState(false);
  if (!row) return null;

  const account = row.account || {};
  const companies = row.companies || [];
  const contractLineItems = row.contract_line_items || [];
  const stripeSubs = row.stripe_subscriptions || [];
  const usage = row.usage_this_month || {};
  const derived = row.derived || {};
  const findings = row.audit_findings || [];
  const contracts = row.contracts || [];
  const stripeCustomerId =
    stripeSubs.find((s) => s.stripe_customer_id)?.stripe_customer_id || null;

  const firstCompany = companies[0];
  const hasMultipleCompanies = companies.length > 1;
  const handleOpenInApp = () => {
    const url = voiceAgentsAppUrl(firstCompany?.id);
    if (url) window.open(url, "_blank", "noopener");
  };

  const handleCopyJson = () => {
    navigator.clipboard?.writeText(JSON.stringify(row, null, 2));
    setJsonCopied(true);
    setTimeout(() => setJsonCopied(false), 1500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "20px", fontWeight: 700, lineHeight: 1.2 }}
            >
              {account.name || "—"}
            </Typography>
            <Typography
              sx={{ fontSize: "13px", color: "rgba(1,12,20,0.6)", mt: 0.5 }}
            >
              {account.account_type === "PLG" ? "PLG" : "Contracted"} ·{" "}
              {companies.length} compan{companies.length === 1 ? "y" : "ies"} ·{" "}
              {derived.total_agents ?? 0} agent
              {derived.total_agents === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <CopyableMono value={account.id} label="account id" />
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Audit findings (top — they're the "alert" payload) */}
          {findings.length > 0 && (
            <Stack spacing={1}>
              {findings.map((f, i) => (
                <Alert
                  key={`${f.code}:${i}`}
                  severity={
                    FINDING_SEVERITY[(f.severity || "").toLowerCase()] || "info"
                  }
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    {f.code.replace(/_/g, " ")}
                  </Typography>
                  <Typography sx={{ fontSize: "12px" }}>{f.message}</Typography>
                </Alert>
              ))}
            </Stack>
          )}

          {/* Account header dates */}
          <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
            <KeyValue
              label="Integration installed"
              value={
                account.installed_at
                  ? moment(account.installed_at).format("MMM D, YYYY")
                  : null
              }
            />
            <KeyValue
              label="Last call activity"
              value={
                account.last_active
                  ? moment(account.last_active).fromNow()
                  : null
              }
            />
            <KeyValue
              label="Trial state"
              value={
                derived.trial_state === "exempt"
                  ? "Exempt (Internal)"
                  : derived.trial_state || "—"
              }
            />
          </Stack>

          {/* Usage */}
          <Box>
            <SectionHeader>Usage this period</SectionHeader>
            <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap" }}>
              <KeyValue label="Used" value={`${usage.used_minutes ?? 0} min`} />
              <KeyValue
                label="Included"
                value={
                  usage.included_minutes ? `${usage.included_minutes} min` : "—"
                }
              />
              <KeyValue
                label="Overage"
                value={
                  usage.overage_minutes > 0
                    ? `${usage.overage_minutes} min over`
                    : "none"
                }
              />
            </Stack>
          </Box>

          {/* All contracts (including cancelled — so cancel_date is visible) */}
          {contracts.length > 0 && (
            <Box>
              <SectionHeader>Contracts ({contracts.length})</SectionHeader>
              <Stack spacing={1}>
                {contracts.map((c) => {
                  const cancelDate = c.cancel_date
                    ? moment(c.cancel_date)
                    : null;
                  const cancelsInFuture =
                    cancelDate && cancelDate.isAfter(moment());
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        p: 1.5,
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        backgroundColor: cancelDate ? "#FFFBEB" : undefined,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ flexWrap: "wrap" }}
                      >
                        <KeyValue
                          label="Contract"
                          value={
                            contractDetailUrl(c.id) ? (
                              <Link
                                href={contractDetailUrl(c.id)}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {c.id.slice(0, 8)}…{" "}
                                <OpenInNewIcon sx={{ fontSize: 12, ml: 0.5 }} />
                              </Link>
                            ) : (
                              c.id
                            )
                          }
                        />
                        <KeyValue
                          label="Signed"
                          value={
                            c.sign_date
                              ? moment(c.sign_date).format("MMM D, YYYY")
                              : null
                          }
                        />
                        <KeyValue
                          label="Cancel date"
                          value={
                            cancelDate ? (
                              <span
                                style={{
                                  color: cancelsInFuture
                                    ? "#92400E"
                                    : "#991B1B",
                                }}
                              >
                                {cancelDate.format("MMM D, YYYY")}
                                {cancelsInFuture && " (future)"}
                              </span>
                            ) : (
                              "—"
                            )
                          }
                        />
                        <KeyValue
                          label="VA line items"
                          value={c.voice_agent_line_items_count}
                        />
                        {c.replaced_contract_id && (
                          <KeyValue
                            label="Replaces"
                            value={
                              <Link
                                href={contractDetailUrl(c.replaced_contract_id)}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {c.replaced_contract_id.slice(0, 8)}…
                              </Link>
                            }
                          />
                        )}
                        {c.amended_contract_id && (
                          <KeyValue
                            label="Amends"
                            value={
                              <Link
                                href={contractDetailUrl(c.amended_contract_id)}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {c.amended_contract_id.slice(0, 8)}…
                              </Link>
                            }
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Contract line items (active contract — drives the slot/minute math) */}
          {contractLineItems.length > 0 && (
            <Box>
              <SectionHeader>Active contract line items</SectionHeader>
              <Stack spacing={1}>
                {contractLineItems.map((li) => (
                  <Stack
                    key={li.id}
                    direction="row"
                    spacing={2}
                    sx={{
                      p: 1.5,
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <KeyValue label="Product" value={li.item_key} mono />
                    <KeyValue
                      label="Qty"
                      value={li.qty != null ? parseInt(li.qty, 10) : null}
                    />
                    <KeyValue label="Min/agent" value={li.per_agent_minutes} />
                    {Number(li.overage_price_per_unit) > 0 && (
                      <KeyValue
                        label="Overage rate"
                        value={`$${li.overage_price_per_unit}/min`}
                      />
                    )}
                    <KeyValue
                      label="Contract signed"
                      value={
                        li.contract_sign_date
                          ? moment(li.contract_sign_date).format("MMM D, YYYY")
                          : null
                      }
                    />
                  </Stack>
                ))}
              </Stack>
              <Typography
                sx={{ fontSize: "12px", color: "rgba(1,12,20,0.6)", mt: 1 }}
              >
                Total: {derived.purchased_qty ?? 0} slots ·{" "}
                {usage.included_minutes ?? 0} included min/mo
              </Typography>
            </Box>
          )}

          {/* Stripe subs */}
          {stripeSubs.length > 0 && (
            <Box>
              <SectionHeader>Stripe subscriptions</SectionHeader>
              <Stack spacing={1}>
                {stripeSubs.map((s) => (
                  <Stack
                    key={s.id}
                    direction="row"
                    spacing={2}
                    sx={{
                      p: 1.5,
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <KeyValue
                      label="Sub ID"
                      value={
                        stripeSubscriptionUrl(s.stripe_subscription_id) ? (
                          <Link
                            href={stripeSubscriptionUrl(
                              s.stripe_subscription_id,
                            )}
                            target="_blank"
                            rel="noopener"
                            sx={{ fontFamily: "monospace", fontSize: "12px" }}
                          >
                            {s.stripe_subscription_id}{" "}
                            <OpenInNewIcon sx={{ fontSize: 12, ml: 0.5 }} />
                          </Link>
                        ) : (
                          s.stripe_subscription_id
                        )
                      }
                    />
                    <KeyValue label="Status" value={s.stripe_status} />
                    <KeyValue label="Product" value={s.product_key} mono />
                    {s.cancel_when && (
                      <KeyValue
                        label="Cancels"
                        value={moment(s.cancel_when).format("MMM D, YYYY")}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* Stripe customer link */}
          {stripeCustomerId && (
            <Box>
              <SectionHeader>Stripe customer</SectionHeader>
              <Link
                href={stripeCustomerUrl(stripeCustomerId)}
                target="_blank"
                rel="noopener"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "13px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {stripeCustomerId}
                <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>
            </Box>
          )}

          {/* Companies + agents */}
          <Box>
            <SectionHeader>Companies & agents</SectionHeader>
            <Stack spacing={2}>
              {companies.length === 0 ? (
                <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>
                  No companies on this account.
                </Typography>
              ) : (
                companies.map((co) => {
                  const callsUrl = callHistoryUrl(co.id);
                  return (
                    <Box
                      key={co.id}
                      sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        p: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1.5 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{ fontSize: "14px", fontWeight: 600 }}
                          >
                            {co.name}
                          </Typography>
                          <CopyableMono value={co.id} label="company id" />
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "rgba(1,12,20,0.6)",
                            }}
                          >
                            {(co.agents || []).length} agent
                            {(co.agents || []).length === 1 ? "" : "s"}
                          </Typography>
                          {callsUrl && (
                            <Link
                              href={callsUrl}
                              target="_blank"
                              rel="noopener"
                              sx={{
                                fontSize: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              call history{" "}
                              <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25 }} />
                            </Link>
                          )}
                        </Stack>
                      </Stack>
                      {(co.agents || []).length === 0 ? (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#9CA3AF",
                            fontStyle: "italic",
                          }}
                        >
                          No agents configured.
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {co.agents.map((a) => {
                            const elUrl = elevenLabsAgentUrl(a.agent_id);
                            const locs = a.locations || [];
                            const agentCallsUrl = callHistoryUrlForAgent(
                              co.id,
                              locs,
                            );
                            return (
                              <Box
                                key={a.id}
                                sx={{ borderTop: "1px solid #F3F4F6", pt: 1 }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                  sx={{ flexWrap: "wrap" }}
                                >
                                  <Typography
                                    sx={{ fontSize: "13px", fontWeight: 500 }}
                                  >
                                    {a.config_name || "(unnamed)"}
                                  </Typography>
                                  <AgentStatusChip status={a.status} />
                                  {locs.length > 1 && (
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: "rgba(1,12,20,0.6)",
                                      }}
                                    >
                                      {locs.length} locations
                                    </Typography>
                                  )}
                                  {a.used_minutes_this_month > 0 && (
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        color: "rgba(1,12,20,0.6)",
                                      }}
                                    >
                                      {a.used_minutes_this_month} min used
                                    </Typography>
                                  )}
                                </Stack>

                                {/* Per-location detail. Each ConfigurationLocation gets its
                                    own row so multi-location agents don't silently drop
                                    phone numbers / suspended-state info. */}
                                {locs.length === 0 ? (
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      color: "rgba(1,12,20,0.6)",
                                      mt: 0.25,
                                    }}
                                  >
                                    no location
                                  </Typography>
                                ) : (
                                  <Stack
                                    spacing={0.25}
                                    sx={{ mt: 0.5, ml: 0.5 }}
                                  >
                                    {locs.map((loc) => (
                                      <Stack
                                        key={loc.id}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                        sx={{ flexWrap: "wrap" }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "12px",
                                            color: "rgba(1,12,20,0.6)",
                                          }}
                                        >
                                          {loc.location?.name
                                            ? `Loc: ${loc.location.name}`
                                            : "no location"}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "12px",
                                            color: "rgba(1,12,20,0.6)",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          {loc.twilio_phone || "no phone"}
                                        </Typography>
                                        {loc.used_minutes_this_month > 0 && (
                                          <Typography
                                            sx={{
                                              fontSize: "11px",
                                              color: "rgba(1,12,20,0.6)",
                                            }}
                                          >
                                            {loc.used_minutes_this_month} min
                                          </Typography>
                                        )}
                                        {loc.trial_suspended_phone && (
                                          <Chip
                                            size="small"
                                            label={`grace (trial): ${loc.trial_suspended_phone}`}
                                            sx={{
                                              bgcolor: "#F3F4F6",
                                              color: "#6B7280",
                                              fontFamily: "monospace",
                                              fontSize: "11px",
                                              height: "18px",
                                            }}
                                          />
                                        )}
                                        {loc.cancel_suspended_phone && (
                                          <Chip
                                            size="small"
                                            label={`grace (cancel): ${loc.cancel_suspended_phone}`}
                                            sx={{
                                              bgcolor: "#F3F4F6",
                                              color: "#6B7280",
                                              fontFamily: "monospace",
                                              fontSize: "11px",
                                              height: "18px",
                                            }}
                                          />
                                        )}
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ mt: 0.5, alignItems: "center" }}
                                >
                                  {elUrl ? (
                                    <Link
                                      href={elUrl}
                                      target="_blank"
                                      rel="noopener"
                                      sx={{
                                        fontSize: "11px",
                                        fontFamily: "monospace",
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {a.agent_id}{" "}
                                      <OpenInNewIcon
                                        sx={{ fontSize: 11, ml: 0.25 }}
                                      />
                                    </Link>
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: "#9CA3AF",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      no agent_id
                                    </Typography>
                                  )}
                                  {agentCallsUrl && (
                                    <Link
                                      href={agentCallsUrl}
                                      target="_blank"
                                      rel="noopener"
                                      sx={{
                                        fontSize: "11px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      · view calls{" "}
                                      <OpenInNewIcon
                                        sx={{ fontSize: 11, ml: 0.25 }}
                                      />
                                    </Link>
                                  )}
                                  {a.agent_last_active && (
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: "#9CA3AF",
                                      }}
                                    >
                                      · last active{" "}
                                      {moment(a.agent_last_active).fromNow()}
                                    </Typography>
                                  )}
                                  {a.cancellation_scheduled_at && (
                                    <Typography
                                      sx={{
                                        fontSize: "11px",
                                        color: "#991B1B",
                                      }}
                                    >
                                      · cancels{" "}
                                      {moment(
                                        a.cancellation_scheduled_at,
                                      ).fromNow()}
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button
          startIcon={jsonCopied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={handleCopyJson}
          sx={{ textTransform: "none" }}
        >
          {jsonCopied ? "Copied" : "Copy as JSON"}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
          {firstCompany && (
            <Tooltip
              title={
                hasMultipleCompanies
                  ? `${companies.length} companies on this account — opens the first (${firstCompany.name || firstCompany.id.slice(0, 8) + "…"})`
                  : ""
              }
            >
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenInApp}
                sx={{ textTransform: "none" }}
              >
                {hasMultipleCompanies
                  ? `Open first of ${companies.length} in app`
                  : "Open in app"}
              </Button>
            </Tooltip>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
